// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Button, Link, Skeleton, Spacer } from "@aviary";
import { StickyNavigation } from "@clinic/shared/components/StickyNavigation/StickyNavigation";
import { AuthButtons } from "@emerson/components/AuthButtons/AuthButtons";
import { ProductSearch } from "@emerson/components/ProductSearch/ProductSearch";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { useEmersonCatalogParams } from "@emerson/hooks/useEmersonCatalogParams/useEmersonCatalogParams";
import { l } from "@emerson/locales/i18n";
import { SvgEmersonLogo } from "@shared/assets/logo";
import { StopImpersonating } from "@shared/components/StopImpersonating/StopImpersonating";
import type { UserIdQueryData } from "@shared/data/queries/UserIdQuery/UserId.query";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useHasCatalogSearchShared } from "@shared/hooks/useHasCatalogSearchShared/useHasCatalogSearchShared";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { getParamsString } from "@shared/hooks/useUrlParams/useUrlParams";
import { useNavigate, useMatch } from "@shared/react-router-dom/react-router-dom";

import { AccountDropdown } from "../AccountDropdown/AccountDropdown";
import { BrandsDropdown } from "../BrandsDropdown/BrandsDropdown";
import { EducationDropdown } from "../EducationDropdown/EducationDropdown";
import { EmersonMiniCart } from "../EmersonMiniCart/EmersonMiniCart";
import { QualityDropdown } from "../QualityDropdown/QualityDropdown";
import { QuickOrderButton } from "../QuickOrderButton/QuickOrderButton";
import { ShopDropdown } from "../ShopDropdown/ShopDropdown";

import * as styles from "./Navigation.styles";

type Props = {
  loading: boolean;
  data: UserIdQueryData;
};

const Navigation = ({ loading, data }: Props) => {
  const { t } = useTranslation();
  const { desktop } = useBreakpoints();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const hasSearch = useHasCatalogSearchShared();
  const [catalogParams] = useEmersonCatalogParams();
  const navigate = useNavigate();

  const matchBrand = useMatch(emersonRoutes.staticSearchResultsPage.staticBrandPage);
  const matchCategory = useMatch(emersonRoutes.staticSearchResultsPage.staticCategoryPage);
  const searchColumnWidth = isUnauthenticated ? 12 : 11;

  // Workaround for displaying only one dropdown at a time, when some of them are focused
  const [openDropdown, setOpenDropdown] = useState<string>(null);

  // filter out emerson order pages custom params (sortBy, date, sortOrder) that should not be in the url
  const filteredCatalogParams = Object.fromEntries(
    Object.entries(catalogParams).filter(
      param => param[0] !== "sortBy" && param[0] !== "date" && param[0] !== "sortOrder"
    )
  );
  const pickedParams = getParamsString(filteredCatalogParams);

  useEffect(() => {
    if (!matchBrand && !matchCategory && hasSearch) {
      navigate(`${emersonRoutes.shop.catalog}?${pickedParams}`);
    }
  }, [hasSearch]);

  if (desktop.lessThan) {
    return null;
  }

  const renderAccountDropdown = () => {
    if (loading) {
      return <Skeleton isCircular type="image" imageSize="medium" />;
    }
    if (data?.viewer?.user) {
      return <AccountDropdown />;
    }
  };

  const toggleCallback = (dropdown: string) => (isOpen: boolean) => {
    setOpenDropdown(isOpen ? dropdown : null);
  };

  const renderQuickOrderButton = () => {
    return (
      <>
        <Spacer />
        <QuickOrderButton />
      </>
    );
  };

  const renderActionButtons = () => {
    if (isUnauthenticated) return <AuthButtons />;

    return (
      <>
        <StopImpersonating isCircular={false} />
        {renderQuickOrderButton()}
        <Spacer />
        <Button to={emersonRoutes.orders.orderHistory} isOutlined>
          {t(l.header.Orders.RecentOrders)}
        </Button>
        <Spacer />
        <EmersonMiniCart />
      </>
    );
  };

  const renderAccount = () =>
    !isUnauthenticated && (
      <Columns.Column columnWidth={1} customCss={styles.accountDropdownColumn}>
        {renderAccountDropdown()}
      </Columns.Column>
    );

  return (
    <StickyNavigation
      customStyles={{ stickyNavigation: styles.stickyNavigation, mainNav: styles.mainNav }}
    >
      <div css={styles.leftContent}>
        <Link to={emersonRoutes.base} css={styles.link}>
          <SvgEmersonLogo css={styles.logo} role="img" aria-label={t(l.header.Logo)} />
        </Link>
      </div>
      <div css={styles.rightContent}>
        <Arrange spacing="none" isVertical css={styles.nav} isFullWidth>
          <Columns customCss={styles.centeredWithoutGap} isMarginless>
            <Columns.Column customCss={styles.withMargin} columnWidth={searchColumnWidth}>
              <ProductSearch hideFilterTags clearAllPriorSearchParam />
            </Columns.Column>
            {renderAccount()}
          </Columns>
          <div css={styles.bottomNav}>
            <div css={styles.bottomNavLeft}>
              <ShopDropdown toggleCallback={toggleCallback} openDropdown={openDropdown} />
              <BrandsDropdown toggleCallback={toggleCallback} openDropdown={openDropdown} />
              <QualityDropdown toggleCallback={toggleCallback} openDropdown={openDropdown} />
              <EducationDropdown toggleCallback={toggleCallback} openDropdown={openDropdown} />
            </div>
            <div css={styles.bottomNavRight}>{renderActionButtons()}</div>
          </div>
        </Arrange>
      </div>
    </StickyNavigation>
  );
};

export { Navigation };
